import { useCallback, useRef, useState } from 'react';

export interface PromiseControls<TResult> {
  resolvePromise: (value?: TResult | PromiseLike<TResult>) => void;
  rejectPromise: (reason?: unknown) => void;
}

export interface CreatePromiseState<TResult> {
  lastCreatedPromise?: Promise<TResult>;
}

export interface CreatePromiseActions<TResult, TParams extends unknown[]> {
  createPromise: (...params: TParams) => Promise<TResult>;
  resolveLastCreatedPromise: PromiseControls<TResult>['resolvePromise'];
  rejectLastCreatedPromise: PromiseControls<TResult>['rejectPromise'];
}

interface PromiseStatics<TResult> {
  resolve?: PromiseControls<TResult>['resolvePromise'];
  reject?: PromiseControls<TResult>['rejectPromise'];
}

export function useCreatePromise<TResult = void, TParams extends unknown[] = unknown[]>(onCreate?: (controls: PromiseControls<TResult>, ...params: TParams) => void): [CreatePromiseState<TResult>, CreatePromiseActions<TResult, TParams>] {
  const { current: statics } = useRef<PromiseStatics<TResult>>({});
  const [lastCreatedPromise, setLastCreatedPromise] = useState<Promise<TResult>>({} as Promise<TResult>);

  const createPromise = useCallback((...params: TParams) => {
    const promise = new Promise<TResult>((resolve, reject) => {
      statics.resolve = resolve;
      statics.reject = reject;
    });

    if (onCreate) {
      onCreate(
        {
          resolvePromise: statics.resolve,
          rejectPromise: statics.reject,
        },
        ...params
      );
    }

    setLastCreatedPromise(promise);

    return promise;
  }, [statics, onCreate, setLastCreatedPromise]);

  const resolveLastCreatedPromise = useCallback((value?: TResult | PromiseLike<TResult>) => {
    if (statics.resolve) {
      statics.resolve(value);
    }
  }, [statics]);

  const rejectLastCreatedPromise = useCallback((reason?: unknown) => {
    if (statics.reject) {
      statics.reject(reason);
    }
  }, [statics]);

  return [
    {
      lastCreatedPromise,
    },
    {
      createPromise,
      resolveLastCreatedPromise,
      rejectLastCreatedPromise,
    },
  ];
}
